import { useState } from "react";
import MiniChart from "./MiniChart";
import { LAYOUTS } from "./constants";
import { apiPost, apiDownloadFile } from "../../../../utils/apiClient";
import HtmlPreviewViewer from "../../../../components/HtmlPreviewViewer";

interface PreviewProps {
  sections: any[];
  theme: any;
  branding?: any;
  reportingPeriods?: any[];
  selectedReportingPeriod?: string | null;
  onReportingPeriodChange?: (period: string) => void;
  dataFiles?: string[];
  reportName?: string;
}

// ─── Preview ───
function Preview({ sections, theme, branding, reportingPeriods = [], selectedReportingPeriod, onReportingPeriodChange, dataFiles, reportName }: PreviewProps) {
  const [viewMode, setViewMode] = useState<"builder" | "html">("builder");
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [pdfChartFormat, setPdfChartFormat] = useState<"png_base64" | "svg_base64">("png_base64");

  const generateHtmlPreview = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiPost('/api/reporting/reports/preview', {
        template: {
          report_name: reportName || "Report",
          sections: sections,
          branding: branding,
          data_files: dataFiles
        },
        chart_format: "svg",
        max_reporting_period: selectedReportingPeriod || undefined
      });
      const data = await response.json();
      if (response.ok) {
        setHtmlContent(data.html_content);
        setViewMode("html");
      } else {
        setError(data.detail || "Failed to generate preview");
      }
    } catch (err: any) {
      setError(err.message || "Error generating preview");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    setLoading(true);
    setError("");
    try {
      await apiDownloadFile(
        '/api/reporting/reports/preview/pdf',
        {
          template: {
            report_name: reportName || "Report",
            sections: sections,
            branding: branding,
            data_files: dataFiles
          },
          chart_format: pdfChartFormat,
          max_reporting_period: selectedReportingPeriod || undefined
        },
        'report_preview.pdf'
      );
    } catch (err: any) {
      setError(err.message || "Error generating PDF");
    } finally {
      setLoading(false);
    }
  };

  const handleViewModeChange = (mode: "builder" | "html") => {
    if (mode === "html" && !htmlContent) {
      generateHtmlPreview();
    } else {
      setViewMode(mode);
    }
  };
  const pages: any[] = [];
  sections.forEach((s: any) => {
    const reps = s.repeatField !== "None" ? [`${s.repeatField} A`, `${s.repeatField} B`, "…"] : [null];
    reps.forEach((rl: any) => s.pages.forEach((p: any, pi: number) => {
      const li = LAYOUTS.find(l => l.id === p.layout) || LAYOUTS[0];
      pages.push({ sec: s.name, rep: rl, page: p, li, isRep: s.repeatField !== "None" });
    }));
  });
  const gColors = [theme.blue, theme.success, theme.warning, theme.error];
  
  // HTML Preview View
  if (viewMode === "html") {
    // Show error state
    if (error) {
      return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: "80vh" }}>
          <div style={{ 
            padding: "12px 20px", 
            borderBottom: `1px solid ${theme.border}`,
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: theme.cardBackground
          }}>
            <button 
              onClick={() => setViewMode("builder")}
              style={{ 
                padding: "6px 14px", 
                borderRadius: 7, 
                fontSize: 13, 
                fontWeight: 600, 
                cursor: "pointer", 
                border: `1px solid ${theme.border}`,
                background: theme.background,
                color: theme.text
              }}
            >
              ← Builder View
            </button>
          </div>
          <div style={{ 
            margin: 20, 
            padding: "12px 16px", 
            borderRadius: 8, 
            background: theme.errorLight, 
            border: `1px solid ${theme.error}`,
            color: theme.error,
            fontSize: 14
          }}>
            ⚠️ {error}
          </div>
        </div>
      );
    }
    
    // Show loading state
    if (loading && !htmlContent) {
      return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: "80vh" }}>
          <div style={{ 
            padding: "12px 20px", 
            borderBottom: `1px solid ${theme.border}`,
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: theme.cardBackground
          }}>
            <button 
              onClick={() => setViewMode("builder")}
              style={{ 
                padding: "6px 14px", 
                borderRadius: 7, 
                fontSize: 13, 
                fontWeight: 600, 
                cursor: "pointer", 
                border: `1px solid ${theme.border}`,
                background: theme.background,
                color: theme.text
              }}
            >
              ← Builder View
            </button>
          </div>
          <div style={{ 
            padding: "60px 20px", 
            textAlign: "center", 
            color: theme.textSecondary 
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Generating HTML Preview...</div>
            <div style={{ fontSize: 14, marginTop: 8 }}>This may take a moment</div>
          </div>
        </div>
      );
    }
    
    // Show HTML preview with shared component
    if (htmlContent) {
      return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: "80vh" }}>
          {/* Additional controls toolbar */}
          <div style={{ 
            padding: "8px 20px", 
            borderBottom: `1px solid ${theme.border}`,
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: theme.cardBackground
          }}>
            <button 
              onClick={() => setViewMode("builder")}
              style={{ 
                padding: "6px 14px", 
                borderRadius: 7, 
                fontSize: 13, 
                fontWeight: 600, 
                cursor: "pointer", 
                border: `1px solid ${theme.border}`,
                background: theme.background,
                color: theme.text
              }}
            >
              ← Builder View
            </button>
            <button 
              onClick={() => generateHtmlPreview()}
              disabled={loading}
              style={{ 
                padding: "6px 14px", 
                borderRadius: 7, 
                fontSize: 13, 
                fontWeight: 600, 
                cursor: loading ? "default" : "pointer", 
                border: "none",
                background: theme.blue,
                color: "white",
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? "Generating..." : "🔄 Refresh HTML"}
            </button>
            {/* Reporting Period Dropdown */}
            {reportingPeriods.length > 0 && onReportingPeriodChange && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 8 }}>
                <label style={{ 
                  fontSize: 12, 
                  fontWeight: 600, 
                  color: theme.textSecondary,
                  whiteSpace: "nowrap"
                }}>
                  Max Period:
                </label>
                <select
                  value={selectedReportingPeriod || ""}
                  onChange={(e) => onReportingPeriodChange(e.target.value)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: "pointer",
                    border: `1px solid ${theme.border}`,
                    background: theme.background,
                    color: theme.text,
                    outline: "none"
                  }}
                >
                  {reportingPeriods.map((period) => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ marginLeft: "auto", fontSize: 13, fontWeight: 600, color: theme.blue }}>
              HTML Preview
            </div>
          </div>
          
          {/* Shared HTML Preview Component */}
          <HtmlPreviewViewer
            htmlContent={htmlContent}
            theme={theme}
            onDownload={downloadPdf}
            title=""
            showBackButton={false}
            showDownloadButton={true}
            downloadButtonLabel={loading ? "Generating..." : "Download as PDF"}
            minHeight="0"
          />
        </div>
      );
    }
  }
  
  // Builder Preview View (default)
  return (
    <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
      {/* View Mode Toggle */}
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between",
        marginBottom: 6
      }}>
        <div style={{ 
          fontSize: 13, 
          fontWeight: 700, 
          textTransform: "uppercase", 
          letterSpacing: ".1em", 
          color: theme.textSecondary
        }}>
          Report Preview · {pages.length} pages
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {/* Reporting Period Dropdown */}
          {reportingPeriods.length > 0 && onReportingPeriodChange && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <label style={{ 
                fontSize: 12, 
                fontWeight: 600, 
                color: theme.textSecondary,
                whiteSpace: "nowrap"
              }}>
                Max Period:
              </label>
              <select
                value={selectedReportingPeriod || ""}
                onChange={(e) => onReportingPeriodChange(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: "pointer",
                  border: `1px solid ${theme.border}`,
                  background: theme.cardBackground,
                  color: theme.text,
                  outline: "none"
                }}
              >
                {reportingPeriods.map((period) => (
                  <option key={period.value} value={period.value}>
                    {period.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button 
            onClick={() => handleViewModeChange("html")}
            disabled={loading}
            style={{ 
              padding: "6px 14px", 
              borderRadius: 7, 
              fontSize: 13, 
              fontWeight: 600, 
              cursor: loading ? "default" : "pointer", 
              border: "none",
              background: theme.buttonBackground,
              color: theme.buttonText,
              display: "flex",
              alignItems: "center",
              gap: 6,
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? "⏳ Loading..." : "👁️ View HTML Preview"}
          </button>
          <select
            value={pdfChartFormat}
            onChange={(e) => setPdfChartFormat(e.target.value as "png_base64" | "svg_base64")}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
              border: `1px solid ${theme.border}`,
              background: theme.cardBackground,
              color: theme.text,
              outline: "none"
            }}
          >
            <option value="png_base64">PNG (Standard)</option>
            <option value="svg_base64">SVG (Compact)</option>
          </select>
          <button
            onClick={downloadPdf}
            disabled={loading}
            style={{
              padding: "6px 14px",
              borderRadius: 7,
              fontSize: 13,
              fontWeight: 600,
              cursor: loading ? "default" : "pointer",
              border: "none",
              background: theme.success || "#10b981",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 6,
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? "⏳ Loading..." : "📥 Download as PDF"}
          </button>
        </div>
      </div>
      {pages.map((item: any, i: number) => (
        <div 
          key={i} 
          style={{ 
            background: theme.cardBackground, 
            borderRadius: 10, 
            padding: 14, 
            border: `1px solid ${theme.border}`, 
            ...(item.isRep ? { borderLeft: `3px solid ${theme.blue}` } : {}) 
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <div>
              <div style={{ 
                fontSize: 11, 
                fontWeight: 600, 
                textTransform: "uppercase", 
                letterSpacing: ".07em", 
                color: theme.textSecondary
              }}>
                {item.sec}{item.rep ? ` · ${item.rep}` : ""}
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, marginTop: 2 }}>
                {item.page.name}
              </div>
            </div>
            <div style={{ 
              fontSize: 11, 
              fontWeight: 600, 
              padding: "2px 8px", 
              borderRadius: 8, 
              background: theme.navbarBackground, 
              color: theme.textSecondary, 
              height: "fit-content" 
            }}>
              p.{i + 1}
            </div>
          </div>
          {(() => {
            const ins = item.page.insights || "none";
            const graphGrid = (
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: `repeat(${item.li.cols}, 1fr)`, 
                gap: 6 
              }}>
                {item.page.graphs.map((g: any, gi: number) => (
                  <div 
                    key={g.id} 
                    style={{ 
                      background: theme.navbarBackground, 
                      borderRadius: 6, 
                      padding: "6px 4px", 
                      textAlign: "center", 
                      border: `1px solid ${theme.border}` 
                    }}
                  >
                    <div style={{ 
                      fontSize: 10, 
                      fontWeight: 600, 
                      color: theme.textSecondary, 
                      marginBottom: 2, 
                      overflow: "hidden", 
                      textOverflow: "ellipsis", 
                      whiteSpace: "nowrap" 
                    }}>
                      {g.label}
                    </div>
                    <MiniChart 
                      type={g.chartType} 
                      w={item.li.cols > 2 ? 75 : item.li.cols > 1 ? 100 : (ins === "right" ? 120 : 180)} 
                      h={item.li.cols > 2 ? 28 : 38} 
                      color={gColors[gi % 4]} 
                    />
                    <div style={{ fontSize: 9, color: theme.textSecondary, marginTop: 1 }}>
                      {g.yAxisField.replace(/_/g, " ")}{g.filterContext !== "None" ? " ⚡" : ""}
                    </div>
                  </div>
                ))}
              </div>
            );
            const insBlock = (horiz: boolean) => (
              <div style={{ 
                background: theme.navbarBackground, 
                borderRadius: 6, 
                padding: "8px 6px", 
                border: `1px solid ${theme.purple}40`, 
                display: "flex", 
                flexDirection: horiz ? "row" : "column", 
                alignItems: "center", 
                justifyContent: "center", 
                gap: 4, 
                minHeight: horiz ? 30 : 50 
              }}>
                <div style={{ 
                  fontSize: 10, 
                  fontWeight: 700, 
                  color: theme.purple, 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 3 
                }}>
                  💡 Insights
                </div>
                <div style={{ fontSize: 9, color: theme.textSecondary, fontStyle: "italic" }}>
                  LLM-generated analysis
                </div>
              </div>
            );
            if (ins === "right") {
              return (
                <div style={{ display: "grid", gridTemplateColumns: "65% 35%", gap: 6 }}>
                  {graphGrid}
                  {insBlock(false)}
                </div>
              );
            }
            if (ins === "bottom") {
              return (
                <>
                  {graphGrid}
                  <div style={{ marginTop: 6 }}>{insBlock(true)}</div>
                </>
              );
            }
            return graphGrid;
          })()}
        </div>
      ))}
    </div>
  );
}

export default Preview;
